import { ReactNode, useEffect, useState } from "react"
import PopupHeader from "./PopupHeader"
import { TableData, TableDataField } from "../Table/DataTable"
import StructureComponent from "./DetailsPopupComponents/StructureComponent";
import { Btn } from "../Buttons/Buttons";
import { ReactComponent as ExpandIcon } from '../../assets/Icons/adornments/Expand.svg';
import PopupFooter from "./PopupFooter";
import { capitalize } from "../../utils/formattingFns";
import CopyToClipbardButton from "../Buttons/CopyToClipboardButton";
import PlotComponent from "./ModelDetailsPopupComponents/PlotComponent";
import StatsValue from "./DetailsPopupComponents/StatsValue";
import NumberSlider from "../Data/NumberSlider";
import GenerateReadAcrossModal from "../../routes/ReadAcross/components/GenerateReadAcrossModal";
import SingleCompoundPredictionModal from "../../routes/Resultsets/components/SingleCompoundPredictionModal";
import BooleanValue from "./DetailsPopupComponents/BooleanValue";
import ModalBase from "./ModalBase";
import { useAccess } from "../../access/useAccess";
import { readacross_access } from "../../access/predefinedConditions";
import { MoleculeHighlightingType } from "../../routes/Resultsets/pages/IndividualResultset";

export default function DetailsModal({
    data,
    threshold,
    isGridItem,
    setPopupMessage,
    moleculeHighlightingData,
    label,
}: {
    data: TableData,
    threshold?: number,
    isGridItem: boolean,
    setPopupMessage: (popupMessage: string, isSuccessMessage: boolean) => void,
    moleculeHighlightingData?: { name: string, data: MoleculeHighlightingType }[],
    label?: string,
}) {
    const { canAccess } = useAccess();

    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const [ visField, setVisField ] = useState<TableDataField | null>(null);
    const [ plotFields, setPlotFields ] = useState<TableDataField[]>([]);
    const [ alphaThreshold, setAlphaThreshold ] = useState<number | null>(null);

    const [ predButtons, setPredButtons ] = useState<ReactNode[]>([]);

    useEffect(() => {
        if (threshold !== undefined) {
            setAlphaThreshold(0.5);
        } else {
            setAlphaThreshold(null);
        }
    }, [threshold]);

    const btns: Btn[] = [
        // {
        //     type: "primary",
        //     styling: "outline",
        //     id: 'generate-read-across-btn',
        //     label: 'Generate Read-Across',
        //     startingAdornment: <ReadAcrossIcon/>,
        // },
        {
            type: 'primary',
            styling: "solid",
            id: 'close-details-popup',
            label: "Close",
            action: handleClose,
        }
    ];


    
    useEffect(() => {
        const visualField = data.fields.find(field => field.type === 'structure');
        if (visualField) {
            setVisField(visualField);
        } else {
            setPlotFields(data.fields.filter(field => field.type === 'plot'));
        }
    }, [data]);

    useEffect(() => {
        if (visField) {
            let buttons = []
            if (canAccess({or: readacross_access})) buttons.push(
                <GenerateReadAcrossModal 
                    location="modal" 
                    selectedCompound={visField.value as string}
                    setPopupMessage={setPopupMessage}
                />,
            );

            buttons.push(
                <SingleCompoundPredictionModal 
                    location="modal"
                    selectedCompound={visField.value as string}
                    setPopupMessage={setPopupMessage}
                />
            );
            setPredButtons(buttons);
        }

        //eslint-disable-next-line
    }, [visField])

    return (
        <div>
            {!isGridItem && label === undefined ? (
                <button onClick={handleOpen} className='w-[18px] text-highlight hover:text-primary transition-all'>
                    <ExpandIcon/>
                </button>
            ) : label !== undefined ? (
                <button 
                    className={`relative z-20 text-left text-[14px] text-nowrap font-medium text-highlight hover:text-primary transition-all max-w-[300px] truncate`} 
                    onClick={handleOpen}
                >
                    {label}
                </button>
            ) : (
                <button onClick={handleOpen} className="w-full bg-[#F7F6FB] text-disable font-semibold text-[12px] p-3 hover:bg-secondary hover:text-primary transition-all">
                    See more details
                </button>
            )}

            <ModalBase
                open={open}
                modalId="details-modal"
            >
                <div className={`bg-white rounded-lg w-[900px] mx-auto`}>
                    <div className='flex flex-col justify-center items-stretch'> 
                        <PopupHeader
                            label={visField && visField.type === 'structure' ? 'Molecule Details' : 'Model Details'}
                            hasOutline={true}
                            closeFn={handleClose}
                        />
    
                        <div className="w-full flex justify-center items-stretch">
                            <>
                            {visField && visField.type === 'structure' ? (
                                <StructureComponent value={visField.value as string} moleculeHighlightingData={moleculeHighlightingData}/>
                            ) : plotFields.length > 0 ? (
                                <PlotComponent plotFields={plotFields}/>
                            ) : (
                                <></>
                            )}
                            </>
                            <div className="grow border-l border-outline w-full max-h-[500px] overflow-y-auto">
                                <div className="w-full flex flex-col p-6 gap-8">
                                    {alphaThreshold && (
                                        <NumberSlider 
                                            value={alphaThreshold}
                                            setValue={setAlphaThreshold}
                                            label="Alpha Threshold"
                                            range={{
                                                min: 0,
                                                max: 1,
                                                step: 0.1,
                                            }}
                                        />
                                    )}
                                    <div className="w-full flex flex-col gap-1 justify-start items-start">
                                        <label className="font-medium text-[12px] text-default flex justify-start items-center gap-2">
                                            SMILES string
                                            {visField && <CopyToClipbardButton copyValue={visField.value as string}/>}
                                        </label>
                                        <p className={`text-default break-all`}>{visField && visField.value}</p>
                                    </div>
                                    <div className="grid grid-cols-2 auto-rows-auto gap-x-6 gap-y-8">
                                        {data.fields.filter(field => field.type !== 'structure' && !field.associatedColumn.toLocaleLowerCase().includes('smile')).map((field, i) => {
                                            const label = field.associatedColumn.split('_').map(word => capitalize(word)).join(' ');
                                            return (
                                                <div key={i} className="w-full flex flex-col gap-1 justify-start items-start">
                                                    <label className="font-medium text-[12px] text-default flex justify-start items-center gap-2">
                                                        {label}
                                                    </label>
                                                    {field.type === 'text' ? (
                                                        <p className={`text-default`}>{field.value}</p>
                                                    ) : field.type === 'stat' ? (
                                                        <StatsValue 
                                                            value={field.value as string}
                                                            threshold={alphaThreshold !== null ? alphaThreshold : undefined}
                                                            isReversed={false}
                                                        />
                                                    ) : field.type === 'boolean' ? (
                                                        <BooleanValue 
                                                            value={field.value as string}
                                                        />
                                                    ) : (
                                                        <></>
                                                    )}
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
    
                        <PopupFooter 
                            hasOutline={true}
                            buttons={btns}
                            predButtons={predButtons}
                        />
                    </div>
                </div>
            </ModalBase>
        </div>
    )
}